/**
 * AuthRepository provides a thin API wrapper around the backend auth endpoints.
 *
 * Responsibilities:
 * - send login credentials to the auth service
 * - refresh existing sessions using refresh tokens
 * - log out the current authenticated user
 * - normalize backend response payloads to the local User model
 */
import { User } from './userModel';
import { API_BASE_URL, AUTH_ENDPOINTS } from '../../../shared/constants';

export const AuthRepository = {
  /**
   * Perform a login request against the backend.
   *
   * @param {{company:string,email:string,password:string}} params
   * @returns {Promise<{success:boolean,user:import('./userModel').User}>}
   * @throws {Error} When the backend response is not successful.
   */
  login: async ({ company, email, password }) => {
    try {
      const response = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.login}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_name: company,
          login: email,
          password,
          fcm_token: 'web',
        }),
      });

      const body = await response.json();

      if (!response.ok || body.success !== true) {
        const error = new Error(body.error?.message || 'Login failed.');
        error.code = body.error?.code;
        throw error;
      }

      return {
        success: true,
        user: User.fromJson(body.data),
      };
    } catch (err) {
      const error = new Error(err.message || 'Network error occurred.');
      error.code = err.code || 'NETWORK_ERROR';
      throw error;
    }
  },

  /**
   * Refresh an existing session using a refresh token.
   *
   * @param {{refreshToken:string}} params
   * @returns {Promise<{success:boolean,user:import('./userModel').User}>}
   * @throws {Error} When refresh fails or backend returns an error payload.
   */
  refresh: async ({ refreshToken }) => {
    const response = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.refresh}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    const body = await response.json();

    if (!response.ok || body.success !== true) {
      const error = new Error(body.error?.message || 'Token refresh failed.');
      error.code = body.error?.code;
      throw error;
    }

    return {
      success: true,
      user: User.fromJson(body.data),
    };
  },

  /**
   * Invalidate the current session on the backend.
   *
   * Sends the access token as an Authorization header and clears the remote session.
   *
   * @param {{accessToken:string|null}} params
   * @returns {Promise<{success:boolean}>}
   * @throws {Error} When logout fails or the backend returns an error response.
   */
  logout: async ({ accessToken }) => {
    const response = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.logout}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const error = new Error(body?.error?.message || 'Logout failed.');
      error.code = body?.error?.code;
      throw error;
    }

    return { success: true };
  },

  resetPassword: async ({ email }) => {
    // TODO: implement password reset API
    return Promise.resolve({ message: 'Password reset email sent.' });
  },
};
